import { sbotSlug } from "@/models/fieldDefinition";
import type { LeagueModel, SportModel } from "@/models/catalog";
import { leagueFirestoreData, sportFirestoreData } from "@/models/catalog";
import type { OrganizationModel } from "@/models/organization";
import { organizationFirestoreData } from "@/models/organization";
import type { LeagueOrganizationAdminValue } from "@/models/schema";
import { organizationAdminValueFirestoreData } from "@/models/schema";
import {
  membershipId,
  resolvedLeagueID,
  userFirestoreData,
  type SBotActiveContext,
  type SBotMembership,
  type SBotUser,
  type SBotUserRole,
} from "@/models/user";
import { trimmedNonEmpty } from "@/models/firestore";
import { apiRequest } from "./functionsClient";

// Port of Services/OrganizationOnboardingService.swift. The server
// (`POST /onboarding/commit`) batch-writes the docs and rebuilds member
// summaries; ISO date strings are converted to Timestamps server-side.

export function customSport(name: string, createdByUserID: string): SportModel {
  const slug = sbotSlug(name);
  return {
    id: slug,
    slug,
    name: name.trim() || name,
    source: "custom",
    reviewStatus: "pendingReview",
    managedBySBotLabs: false,
    createdByUserID,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export function customLeague(sportID: string, name: string, abbreviation: string, creatorUserID: string): LeagueModel {
  const slug = sbotSlug(name);
  return {
    id: `${sportID}_${slug}`,
    sportID,
    slug,
    name: name.trim() || name,
    abbreviation: abbreviation.trim(),
    source: "custom",
    reviewStatus: "pendingReview",
    managedBy: "creator",
    creatorUserID,
    adminUserIDs: [creatorUserID],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export function customOrganization(
  sportID: string,
  leagueID: string,
  name: string,
  abbreviation: string,
  location: string,
  createdByUserID: string,
): OrganizationModel {
  const slug = sbotSlug(name);
  return {
    id: `${leagueID}_${slug}`,
    sportID,
    leagueID,
    name: name.trim() || name,
    abbreviation: trimmedNonEmpty(abbreviation),
    location: trimmedNonEmpty(location),
    source: "custom",
    reviewStatus: "pendingReview",
    createdByUserID,
    adminUserIDs: [],
    managerUserIDs: [],
    staffUserIDs: [],
    coachUserIDs: [],
    athleteUserIDs: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export interface OrganizationOnboardingRequest {
  user: SBotUser;
  role: SBotUserRole;
  sport: SportModel;
  shouldSaveSport: boolean;
  league: LeagueModel;
  shouldSaveLeague: boolean;
  organization: OrganizationModel | null;
  shouldSaveOrganization: boolean;
  organizationAdminValue: LeagueOrganizationAdminValue | null;
  inviteCode: string | null;
}

function appendingUnique<T>(item: T, values: T[]): T[] {
  return values.includes(item) ? values : [...values, item];
}

function upsertMembership(memberships: SBotMembership[], membership: SBotMembership): SBotMembership[] {
  const index = memberships.findIndex((m) => membershipId(m) === membershipId(membership));
  if (index >= 0) {
    const next = [...memberships];
    next[index] = membership;
    return next;
  }
  return [...memberships, membership];
}

function activeContextFor(membership: SBotMembership, fallbackDisplayName: string): SBotActiveContext {
  return {
    targetType: membership.targetType,
    targetID: membership.targetID,
    displayName: trimmedNonEmpty(membership.displayName) ?? fallbackDisplayName,
    sportID: membership.sportID,
    leagueID: resolvedLeagueID(membership),
    organizationID: membership.targetType === "organization" ? (membership.organizationID ?? membership.targetID) : undefined,
    role: membership.role,
  };
}

/** Mirrors OrganizationOnboardingService.completeOnboarding. Returns the committed user. */
export async function completeOnboarding(request: OrganizationOnboardingRequest): Promise<SBotUser> {
  const timestamp = new Date();
  const sport: SportModel = { ...request.sport, updatedAt: timestamp };
  let league: LeagueModel = { ...request.league, updatedAt: timestamp };
  let organization: OrganizationModel | null = request.organization ? { ...request.organization, updatedAt: timestamp } : null;

  if (organization && !organization.createdByUserID) {
    organization = { ...organization, createdByUserID: request.user.id };
  }

  if (request.role === "admin") {
    league = { ...league, adminUserIDs: appendingUnique(request.user.id, league.adminUserIDs) };
    if (organization) {
      organization = { ...organization, adminUserIDs: appendingUnique(request.user.id, organization.adminUserIDs) };
    }
  } else if (request.role === "organizationManager" && organization) {
    organization = { ...organization, managerUserIDs: appendingUnique(request.user.id, organization.managerUserIDs) };
  } else if (request.role === "organizationStaff" && organization) {
    organization = { ...organization, staffUserIDs: appendingUnique(request.user.id, organization.staffUserIDs) };
  } else if (request.role === "coach" && organization) {
    organization = { ...organization, coachUserIDs: appendingUnique(request.user.id, organization.coachUserIDs) };
  } else if (request.role === "athlete" && organization) {
    organization = { ...organization, athleteUserIDs: appendingUnique(request.user.id, organization.athleteUserIDs) };
  }

  const inviteCode = trimmedNonEmpty(request.inviteCode ?? undefined);
  const status = inviteCode ? "pendingInvite" : "active";
  const targetType = organization ? "organization" : "league";
  const targetID = organization?.id ?? league.id;
  const targetName = organization?.name ?? league.name;

  const membership: SBotMembership = {
    targetType,
    targetID,
    role: request.role,
    status,
    sportID: sport.id,
    leagueID: league.id,
    organizationID: organization?.id,
    displayName: targetName,
    inviteCode,
    createdAt: timestamp,
    updatedAt: timestamp,
  };

  const shouldGrantCreatedLeagueAdmin =
    request.role === "admin" && request.shouldSaveLeague && request.shouldSaveOrganization && organization !== null && status === "active";
  const createdLeagueAdminMembership: SBotMembership | null = shouldGrantCreatedLeagueAdmin
    ? {
        targetType: "league",
        targetID: league.id,
        role: "admin",
        status: "active",
        sportID: sport.id,
        leagueID: league.id,
        displayName: league.name,
        createdAt: timestamp,
        updatedAt: timestamp,
      }
    : null;

  let memberships = request.user.memberships;
  if (createdLeagueAdminMembership) {
    memberships = upsertMembership(memberships, createdLeagueAdminMembership);
  }
  memberships = upsertMembership(memberships, membership);

  const user: SBotUser = {
    ...request.user,
    roles: appendingUnique(request.role, request.user.roles),
    memberships,
    activeContext: activeContextFor(
      createdLeagueAdminMembership ?? membership,
      createdLeagueAdminMembership ? league.name : targetName,
    ),
    onboardingStatus: "complete",
    favoriteSports: [sport.name],
    favoriteTeams: [organization?.name ?? league.name],
    updatedAt: timestamp,
  };

  await apiRequest("/onboarding/commit", {
    body: {
      user: userFirestoreData(user),
      sport: request.shouldSaveSport ? sportFirestoreData(sport) : undefined,
      league: request.shouldSaveLeague ? leagueFirestoreData(league) : undefined,
      organization: request.shouldSaveOrganization && organization ? organizationFirestoreData(organization) : undefined,
      organizationAdminValue: request.organizationAdminValue
        ? organizationAdminValueFirestoreData(request.organizationAdminValue)
        : undefined,
    },
  });

  return user;
}
